import { AsyncPipe, CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { LogEvent, SetEventProperties } from '@app/core/states/event-tracking.actions';
import { SetOnboardingModal } from '@app/core/states/onboarding.actions';
import { OnboardingState } from '@app/core/states/onboarding.state';
import { UnsavedChangesService } from '@app/features/app-content/services/unsaved-changes.service';
import { PostAppConfigRequest } from '@app/features/app-content/states/app-configuration.actions';
import { AppConfigurationState } from '@app/features/app-content/states/app-configuration.state';
import { UpdateAssetsRequest } from '@app/features/app-settings/states/assets.actions';
import { AssetsState } from '@app/features/app-settings/states/assets.state';
import { UiModule } from '@app/ui';
import { UIState } from '@app/ui/states/ui.state';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslatePipe } from '@ngx-translate/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { NextStep, PreviousStep } from '../states/stepper.actions';
import { Step, StepperState } from '../states/stepper.state';

@Component({
  selector: 'rk-stepper',
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
  imports: [CommonModule, UiModule, AsyncPipe, TranslatePipe, FontAwesomeModule],
})
export class StepperComponent implements OnInit, OnDestroy {
  @Input()
  customNextStep?: () => void;

  @Input()
  nextStepIsDisabled?: boolean;

  @Input()
  hidePreviousStep?: boolean;

  @Input()
  hideNextStep?: boolean;

  @Input()
  customNextStepText?: string;

  @Select(StepperState.steps)
  steps$: Observable<Step[]>;

  @Select(StepperState.stepsLength)
  stepsLength$: Observable<number>;

  @Select(StepperState.isLastStep)
  isLastStep$: Observable<boolean>;

  @Select(UIState.isCreationProcess)
  isCreationProcess$: Observable<boolean>;

  @Select(AppConfigurationState.appConfigIsLoaded)
  appConfigIsLoaded$: Observable<boolean>;

  @Select(StepperState.currentStep)
  currentStep$: Observable<number>;

  @Select(StepperState.tooltipMessage)
  tooltipMessage$: Observable<string>;

  @Select(AppConfigurationState.isLoading)
  isLoading$: Observable<boolean>;

  @Select(AssetsState.isLoading)
  isLoadingAssets$: Observable<boolean>;

  @Select(OnboardingState.currentStep)
  onboardingCurrentStep$: Observable<number>;

  currentStep: number;
  tooltipFitContentStyle = { width: 'max-content', 'min-width': 'auto' };

  private readonly subscriptions: Subscription = new Subscription();

  @ViewChild('stepper', { static: true }) stepperRef!: ElementRef;

  constructor(
    private readonly unsavedChangesService: UnsavedChangesService,
    private readonly store: Store,
  ) {}

  @Dispatch()
  nextStep() {
    return new NextStep();
  }

  @Dispatch()
  previousStep() {
    return new PreviousStep();
  }

  @Dispatch()
  saveConfig() {
    return new PostAppConfigRequest();
  }

  @Dispatch()
  updateAssets() {
    return new UpdateAssetsRequest();
  }

  ngOnInit() {
    this.subscriptions.add(
      combineLatest([
        this.currentStep$,
        this.onboardingCurrentStep$,
        this.isCreationProcess$,
      ]).subscribe(([currentStep, onboardingCurrentStep, isCreationProcess]) => {
        if (currentStep === 1 && onboardingCurrentStep === 0 && isCreationProcess) {
          this.store.dispatch(new SetOnboardingModal('ModalTrackProgression', true));
        }
      }),
    );
    this.subscriptions.add(
      this.currentStep$.subscribe(step => (this.currentStep = step)),
    );
  }

  next() {
    if (this.customNextStep) {
      this.customNextStep();
    } else {
      this.nextStep();
    }

    // event tracking
    this.store.dispatch(
      new SetEventProperties({
        name: 'StepperNext',
        component: 'Stepper',
        current_step: this.currentStep,
      }),
    );
    this.store.dispatch(new LogEvent('Stepper Clicked'));
  }

  previous() {
    this.subscriptions.add(
      this.unsavedChangesService.confirmDiscardChanges().subscribe(canNavigate => {
        if (canNavigate) {
          this.previousStep();
        }
      }),
    );

    // event tracking
    this.store.dispatch(
      new SetEventProperties({
        name: 'StepperPrevious',
        component: 'Stepper',
      }),
    );
    this.store.dispatch(new LogEvent('Stepper Clicked'));
  }

  save() {
    if (this.currentStep === 1) {
      this.saveConfig();
    } else if (this.currentStep === 2) {
      this.updateAssets();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
